'use memo';

import { useCallback, useEffect, useMemo } from 'react';
import type { DeviceGenre } from '@/constants/device';
import { CFormSectionDetailLayout } from '@/features/c-form/CFormSectionDetailLayout';
import { isMultiSectionAllowedKey } from '@/features/device/multi-section-keys';
import type { DeviceDetailSection, DeviceSectionFormData } from './-query/type';
import { useDeviceDetailFormStore } from './-store/store';
import { DeviceDetailAttributeRenderer } from './AttributeRenderer';
interface DeviceDetailSectionFormProps<T extends DeviceSectionFormData> {
  data: T | undefined;
  isPending: boolean;
  error?: Error | null;
  /** 廃棄済みなど、セクションの編集・保存 UI を出さない（閲覧のみ） */
  readOnly?: boolean;
  /** サイドバーナビを表示するか（タブ内利用などで false） */
  showNavigation?: boolean;
  saveMutateAsync: (request: T) => Promise<unknown>;
  /** Profile編集でカタログ候補を同一ジャンルへ限定する。 */
  deviceGenre?: DeviceGenre;
}
export function DeviceDetailSectionForm<T extends DeviceSectionFormData>({
  data,
  isPending,
  error = null,
  readOnly = false,
  showNavigation = true,
  saveMutateAsync,
  deviceGenre
}: DeviceDetailSectionFormProps<T>) {
  const {
    actions,
    values
  } = useDeviceDetailFormStore();
  useEffect(() => {
    if (data) {
      actions.reset(data);
    }
  }, [data, actions]);
  const handleSave = useCallback(async () => {
    if (!actions.validate()) {
      throw new Error('Validation failed');
    }
    await saveMutateAsync(actions.getUpdateRequest() as T);
  }, [actions, saveMutateAsync]);
  const handleReset = useCallback(() => {
    if (data) {
      actions.reset(data);
    }
  }, [actions, data]);
  const sections = useMemo(() => {
    if (values.sections && values.sections.length > 0) return values.sections;
    return data?.sections;
  }, [values.sections, data?.sections]);
  const renderSectionContent = useCallback((section: DeviceDetailSection) => <DeviceDetailAttributeRenderer attributes={section.attributes} sectionKey={String(section.sectionKey)} displayOrder={section.displayOrder} deviceGenre={deviceGenre} />, [deviceGenre]);
  return <CFormSectionDetailLayout sections={sections} isLoading={isPending || !data} error={error} linkTo="." itemLayout="card" groupBySectionKey isMultiSectionAllowed={isMultiSectionAllowedKey} onAddSection={readOnly ? undefined : actions.addSection} onRemoveSection={readOnly ? undefined : actions.removeSection} onSave={readOnly ? undefined : handleSave} onReset={readOnly ? undefined : handleReset} renderSectionContent={renderSectionContent} showNavigation={showNavigation} />;
}